// src/components/vendor/ItemSelection.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Package, X } from 'lucide-react';
import ProductSearch from './pickup/ProductSearch';
import type { Product, ProductVariant } from '../../types';
import { vendorAPI } from '../../api/vendor';
import { getProductImageUrl } from '../../utils/productUtilities';

interface ItemSelectionProps {
    selectedProducts: any[];
    onChange: (products: any[]) => void;
    invalidProductIds: number[]
}

const ItemSelection: React.FC<ItemSelectionProps> = ({
    selectedProducts,
    onChange,
    invalidProductIds = [],
}) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);
    const [hasFocused, setHasFocused] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);
    const searchWrapperRef = useRef<HTMLDivElement>(null);
    const [showVariant, setShowVariant] = useState<number>();


    const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

    useEffect(() => {
        if (invalidProductIds.length === 0) return;
        const first = invalidProductIds[0];
        rowRefs.current[first]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [invalidProductIds.join(',')]);

    // Debounced search
    useEffect(() => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        if (!searchQuery.trim()) {
            setProducts([]);
            setShowResults(false);
            setHasFocused(false);
            return;
        }

        searchTimeout.current = setTimeout(() => {
            fetchProducts(searchQuery);
        }, 300);

        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, [searchQuery]);

    useEffect(() => {
        fetchProducts('');
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
                setShowResults(false);
                setHasFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (invalidProductIds.length === 0) return;
        rowRefs.current[invalidProductIds[0]]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [invalidProductIds.join(',')]);
    
    const fetchProducts = async (query: string) => {
        setLoadingProducts(true);
        try {
            const params = new URLSearchParams();
            params.append('status', 'approved');
            params.append('order_by', 'quantity_available'); // Lowest stock first

            if (query.trim()) {
                params.append('search', query.trim());
            } else {
                params.append('limit', '20'); // Show top 20 by default
            }

            const response = await vendorAPI.getProductsForPickup(params);
            setProducts(response.data);
            setHasLoaded(true);
        } catch (error) {
            console.error('Failed to fetch products:', error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const addProduct = (product: Product) => {
        const existing = selectedProducts.find(p => p.id === product.id);
        if (existing) { //this is never called
        //     // has variants? ignore top-level click for multi-variant
        //     const hasVariants = product.variants && product.variants.length > 1;
        //     if (hasVariants) return; // user picks variants individually
        //     onChange(selectedProducts.map(p =>
        //         p.id === product.id
        //             ? { ...p, _pickup_quantity: (p._pickup_quantity || 1) + 1 }
        //             : p
        //     ));
        } else {
            onChange([...selectedProducts, { ...product, _pickup_quantity: 0, _variant_quantities: {} }]);
        }
        // handleClearSearch();
    };

    const removeProduct = (productId: number) => {
        onChange(selectedProducts.filter(p => p.id !== productId));
    };

    const updateVariantQuantity = (productId: number, variantId: number, quantity: number) => {
        onChange(selectedProducts.map(p => {
            if (p.id !== productId) return p;
            return {
                ...p,
                _variant_quantities: {
                    ...(p._variant_quantities || {}),
                    [variantId]: quantity,
                },
            };
        }));
    };

    const updateQuantity = (productId: number, quantity: number) => {
        onChange(selectedProducts.map(p =>
            p.id === productId ? { ...p, _pickup_quantity: quantity } : p
        ));
    };

    const handleFocus = () => {
        if (!loadingProducts && hasLoaded) {
            setHasFocused(true);
            // If no search query, show top products
            if (!searchQuery.trim() && products.length === 0) {
                fetchProducts('');
            }
            // Show results if there are products
            if (products.length > 0) {
                setShowResults(true);
            }
        }
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setShowResults(false);
        setHasFocused(false);
    };

    // summaries
    const totalProducts = selectedProducts.reduce((acc, p) => {
        const hasVariants = p.variants && p.variants.length > 1;
        if (hasVariants) {
            const total = Object.values(p._variant_quantities || {}).reduce(
                (a: number, b: any) => a + (b as number), 0
            );
            return acc + (total as number);
        }
        return acc + (p._pickup_quantity || 1);
    }, 0);


    return (
        <div className="space-y-6">
            {/* Product Selection */}
            <ProductSearch
                onSelect={(product) => {
                    addProduct(product)
                    handleClearSearch();
                }}
                selectedProducts={selectedProducts}
                placeholder="Search products to add..."
                hasLoaded={hasLoaded}
                onBlur={() => setHasFocused(false)}
                onFocus={handleFocus}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onClearSearch={handleClearSearch}
                hasFocused={hasFocused}
                wrapperRef={searchWrapperRef}
                showResults={showResults}
                loading={loadingProducts}
                // disabled={loadingProducts}
                products={products}
            />
            {selectedProducts.length === 0 && !searchQuery && (
                <div className="flex flex-col items-center justify-center py-14 text-center">
                    <div className="p-4 bg-gray-100 rounded-full mb-4">
                        <Package className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">No products selected</p>
                    <p className="text-xs text-gray-500 mt-1 max-w-xs">
                        Search for a product above and add it to this pickup request.
                    </p>
                </div>
            )}
            {/* Selected Products Summary */}
            {selectedProducts.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                        Selected ({totalProducts} {totalProducts === 1 ? 'product' : 'products'})
                    </p>
                    <div className="space-y-2">
                        {selectedProducts.map((selected) => {
                            const product = products.find(p => p.id === selected.id);
                            if (!product) return null;

                            // Get primary image or first image
                            const primaryImage = product.images?.find(img => img.is_primary);
                            const displayImage = primaryImage || product.images?.[0];

                            // Check if product has variants
                            const hasVariants = product.variants && product.variants.length > 0;
                            const isMultiVariant = hasVariants && product.variants.length > 1;

                            // For multi-variant, calculate total quantity from variants
                            const totalQuantity = isMultiVariant
                                ? (selected._variant_quantities ? Object.values(selected._variant_quantities).reduce((a, b) => a + b, 0) : 0)
                                : (selected._pickup_quantity || 1);

                            return (
                                <div
                                    ref={el => { rowRefs.current[selected.id] = el; }}
                                    className={`bg-gray-50 rounded-lg border overflow-hidden hover:bg-gray-100 ${invalidProductIds.includes(selected.id)
                                        ? 'border-red-300 bg-red-50'
                                        : 'border-gray-200'
                                        }`}
                                    key={selected.id}
                                >
                                    {/* Main row - clickable for multi-variant */}
                                    <div
                                        className={`flex items-center gap-3 p-2 ${isMultiVariant ? 'cursor-pointer' : ''}`}
                                        onClick={() => isMultiVariant && setShowVariant(selected.id === showVariant ? '' : selected.id)}
                                    >
                                        {/* Product Image */}
                                        <div className="flex-shrink-0">
                                            {displayImage?.image_url ? (
                                                <img
                                                    src={getProductImageUrl(displayImage.image_url)}
                                                    alt={product.title}
                                                    className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                                                    <Package className="h-5 w-5 text-gray-400" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Product Info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900">{product.title}</p>
                                            <p className="text-xs text-gray-500">SKU: {product.sku}</p>
                                            {isMultiVariant && (
                                                <p className="text-xs text-gray-400">
                                                    {product.variants.length} variants · Tap to select
                                                </p>
                                            )}
                                        </div>

                                        {/* Quantity & Remove */}
                                        <div className="flex items-center gap-2">
                                            {isMultiVariant ? (
                                                <span className="text-sm font-medium text-gray-700 min-w-[40px] text-center">
                                                    {totalQuantity}
                                                </span>
                                            ) : (
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={selected._pickup_quantity || 0}
                                                    onChange={(e) => updateQuantity(selected.id, parseInt(e.target.value) || 0)}
                                                    className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                                                />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removeProduct(selected.id)}
                                                className="p-1 text-red-400 hover:text-red-600 rounded hover:bg-red-50"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                    {invalidProductIds.includes(selected.id) && (
                                        <p className="text-xs text-red-600 px-2 pb-2">
                                            Set a quantity greater than 0 for pickup
                                        </p>
                                    )}
                                    {/* Variants list - expandable for multi-variant */}
                                    {isMultiVariant && showVariant === product.id && (
                                        <div className="border-t border-gray-200 bg-white p-2 max-h-48 overflow-y-auto">
                                            <div className="space-y-1.5">
                                                {product.variants?.map((variant: ProductVariant) => {
                                                    const variantQuantity = selected._variant_quantities?.[variant.id] || 0;

                                                    // Get variant image or product image if use_product_images is true
                                                    let variantImage = variant.images?.[0]?.image_url;
                                                    if (variant.use_product_images && displayImage?.image_url) {
                                                        variantImage = displayImage.image_url;
                                                    }
                                                    return (
                                                        <div key={variant.id} className="flex items-center gap-3 px-2 py-1.5 hover:bg-gray-50 rounded">
                                                            {/* Variant Image */}
                                                            <div className="flex-shrink-0">
                                                                {variantImage ? (
                                                                    <img
                                                                        src={getProductImageUrl(variantImage)}
                                                                        alt={variant.name}
                                                                        className="w-8 h-8 rounded object-cover border border-gray-200"
                                                                    />
                                                                ) : (
                                                                    <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center border border-gray-200">
                                                                        <Package className="h-4 w-4 text-gray-400" />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Variant Info */}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm text-gray-700">{variant.name}</p>
                                                                <p className="text-xs text-gray-400">SKU: {variant.sku_suffix}</p>
                                                            </div>

                                                            {/* Quantity Input */}
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    value={variantQuantity}
                                                                    onChange={(e) => {
                                                                        const qty = parseInt(e.target.value) || 0;
                                                                        updateVariantQuantity(selected.id, variant.id, qty);
                                                                    }}
                                                                    className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                                                                />
                                                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                                                    avail: {variant.available_quantity || 0}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <div className="text-xs text-gray-500 text-right pt-1 border-t border-gray-100">
                                                    Total: {totalQuantity} items
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ItemSelection;